import React, { useState, useEffect } from 'react';
import { subscription, dashboard } from '../api';
import { usePricingCurrency } from '../../utils/pricingCurrency';
import { 
  CreditCard, 
  Check, 
  ExternalLink, 
  AlertCircle,
  Loader2,
  Crown,
  Zap,
  Building2,
  Package,
  Eye,
} from 'lucide-react';

const planIcons = {
  free: Package,
  starter: Zap,
  advanced: Crown,
  business: Building2,
};

const planColors = {
  free: 'bg-gray-500/20 text-gray-400',
  starter: 'bg-blue-500/20 text-blue-400',
  advanced: 'bg-purple-500/20 text-purple-400',
  business: 'bg-amber-500/20 text-amber-400',
};

export default function SubscriptionPage() {
  const [status, setStatus] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAnnual, setIsAnnual] = useState(false);
  const [loadingCheckout, setLoadingCheckout] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [usage, setUsage] = useState({ products: 0 });
  const { formatUsd } = usePricingCurrency();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [statusData, plansData, statsData] = await Promise.all([
        subscription.getStatus(),
        subscription.getPlans(),
        dashboard.getStats(),
      ]);
      setStatus(statusData);
      setPlans(plansData.plans);
      setUsage({ products: statsData.products || 0 });
    } catch (err) {
      console.error('Failed to load subscription data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async (planId) => {
    if (planId === 'business') {
      window.location.href = 'mailto:sales@vltryon.com?subject=VisionLive Business Plan Inquiry';
      return;
    }

    setLoadingCheckout(planId);
    try {
      const { checkoutUrl } = await subscription.checkout(
        planId,
        isAnnual ? 'annual' : 'monthly'
      );
      window.location.href = checkoutUrl;
    } catch (err) {
      console.error('Checkout error:', err);
      alert('Failed to start checkout. Please try again.');
    } finally {
      setLoadingCheckout(null);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? You will retain access until the end of your billing period.')) {
      return;
    }

    setCancelling(true);
    try {
      await subscription.cancel();
      await loadData(); // Refresh status
    } catch (err) {
      console.error('Cancel error:', err);
      alert('Failed to cancel subscription. Please try again or contact support.');
    } finally {
      setCancelling(false);
    }
  };

  const handleManageBilling = async () => {
    try {
      const { portalUrl } = await subscription.getPortalUrl();
      window.open(portalUrl, '_blank');
    } catch (err) {
      console.error('Portal error:', err);
      alert('Failed to open billing portal.');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-6 text-center">
        <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-2" />
        <p className="text-red-400">{error}</p>
        <button
          onClick={loadData}
          className="mt-4 px-4 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30"
        >
          Try Again
        </button>
      </div>
    );
  }

  const currentPlan = status?.plan || 'free';
  const PlanIcon = planIcons[currentPlan] || Package;
  const isSubscribed = currentPlan !== 'free' && status?.status === 'active';

  return (
    <div className="space-y-8">
      {/* Current Plan */}
      <div className="bg-[#1a1a1a] rounded-2xl border border-white/10 p-6">
        <h2 className="text-lg font-semibold text-white mb-6">Current Plan</h2>
        
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className={`w-14 h-14 rounded-xl ${planColors[currentPlan]} flex items-center justify-center`}>
              <PlanIcon className="w-7 h-7" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-white capitalize">{currentPlan}</h3>
              {status?.status && (
                <p className={`text-sm ${status.status === 'active' ? 'text-green-400' : 'text-yellow-400'}`}>
                  {status.status === 'active' ? 'Active' : status.status}
                  {status.billingCycle && ` • ${status.billingCycle}`}
                </p>
              )}
              {status?.periodEnd && (
                <p className="text-sm text-white/50">
                  {status.status === 'cancelled' ? 'Access until: ' : 'Renews: '}
                  {new Date(status.periodEnd).toLocaleDateString()}
                </p>
              )}
            </div>
          </div>

          {isSubscribed && (
            <div className="flex gap-3">
              <button
                onClick={handleManageBilling}
                className="px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors flex items-center gap-2"
              >
                <CreditCard className="w-4 h-4" />
                Manage Billing
                <ExternalLink className="w-3 h-3" />
              </button>
              {status.status !== 'cancelled' && (
                <button
                  onClick={handleCancel}
                  disabled={cancelling}
                  className="px-4 py-2 text-red-400 hover:text-red-300 transition-colors disabled:opacity-50"
                >
                  {cancelling ? 'Cancelling...' : 'Cancel'}
                </button>
              )}
            </div>
          )}
        </div>

        {/* Usage */}
        {status?.limits && (
          <div className="mt-6 pt-6 border-t border-white/10">
            <h4 className="text-sm font-medium text-white/60 mb-4">Usage</h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-[#252525] rounded-xl p-4">
                <div className="flex items-center gap-2 text-white/60 text-sm mb-1">
                  <Package className="w-4 h-4" />
                  Products
                </div>
                <p className="text-2xl font-bold text-white">
                  {usage.products}
                  <span className="text-sm text-white/40 font-normal">
                    {' / '}{status.limits.products === -1 ? 'Unlimited' : status.limits.products}
                  </span>
                </p>
              </div>
              <div className="bg-[#252525] rounded-xl p-4">
                <div className="flex items-center gap-2 text-white/60 text-sm mb-1">
                  <Eye className="w-4 h-4" />
                  VTO Sessions
                </div>
                <p className="text-2xl font-bold text-white">
                  <span className="text-sm text-white/40 font-normal">
                    {status.limits.vtoSessions === -1 ? 'Unlimited' : `${status.limits.vtoSessions.toLocaleString()} / mo`}
                  </span>
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Available Plans */}
      <div>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-white">
            {isSubscribed ? 'Change Plan' : 'Upgrade Your Plan'}
          </h2>
          
          {/* Billing Toggle */}
          <div className="flex items-center gap-3 bg-[#1a1a1a] rounded-full p-1 border border-white/10">
            <button
              onClick={() => setIsAnnual(false)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                !isAnnual ? 'bg-blue-500 text-white' : 'text-white/60 hover:text-white'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setIsAnnual(true)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                isAnnual ? 'bg-blue-500 text-white' : 'text-white/60 hover:text-white'
              }`}
            >
              Discounted
              <span className="px-1.5 py-0.5 rounded bg-green-500/20 text-green-400 text-xs">
                Save up to 24%
              </span>
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {plans.map((plan) => {
            const isCurrent = plan.id === currentPlan;
            const isDowngrade = 
              (currentPlan === 'advanced' && plan.id === 'starter') ||
              (currentPlan !== 'free' && plan.id === 'free');
            const Icon = planIcons[plan.id] || Package;

            return (
              <div
                key={plan.id}
                className={`relative rounded-2xl p-6 border transition-all ${
                  plan.popular
                    ? 'bg-[#1a1a1a] border-blue-500'
                    : 'bg-[#1a1a1a] border-white/10'
                } ${isCurrent ? 'ring-2 ring-green-500' : ''}`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="px-3 py-1 rounded-full bg-blue-500 text-xs font-bold text-white">
                      Most Popular
                    </span>
                  </div>
                )}

                {isCurrent && (
                  <div className="absolute -top-3 right-4">
                    <span className="px-3 py-1 rounded-full bg-green-500 text-xs font-bold text-white">
                      Current
                    </span>
                  </div>
                )}

                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-10 h-10 rounded-lg ${planColors[plan.id]} flex items-center justify-center`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="text-lg font-bold text-white">{plan.name}</h3>
                </div>

                {plan.monthlyPrice !== null ? (
                  <div className="mb-4">
                    <div className="flex items-baseline gap-1">
                      <span className="text-3xl font-bold text-white">
                        {formatUsd(isAnnual ? plan.annualPrice : plan.monthlyPrice).formatted}
                      </span>
                      <span className="text-white/50">/mo</span>
                    </div>
                    {isAnnual && (
                      <p className="text-sm text-white/50">
                        Billed {formatUsd(plan.annualTotal).formatted}/{plan.id === 'advanced' ? '6 months' : 'year'}
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="mb-4">
                    <span className="text-2xl font-bold text-white">Custom</span>
                    <p className="text-sm text-white/50">Contact for pricing</p>
                  </div>
                )}

                <ul className="space-y-2 mb-6">
                  {plan.features.slice(0, 4).map((feature) => (
                    <li key={feature} className="flex items-start gap-2 text-sm text-white/70">
                      <Check className="w-4 h-4 text-green-400 shrink-0 mt-0.5" />
                      {feature}
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => handleUpgrade(plan.id)}
                  disabled={isCurrent || loadingCheckout === plan.id || plan.id === 'free'}
                  className={`w-full py-3 rounded-xl font-semibold transition-all ${
                    isCurrent
                      ? 'bg-green-500/20 text-green-400 cursor-default'
                      : plan.id === 'free'
                      ? 'bg-white/5 text-white/40 cursor-not-allowed'
                      : plan.popular
                      ? 'bg-blue-500 text-white hover:bg-blue-600'
                      : 'bg-white/10 text-white hover:bg-white/20'
                  } disabled:opacity-50`}
                >
                  {loadingCheckout === plan.id ? (
                    <span className="flex items-center justify-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Processing...
                    </span>
                  ) : isCurrent ? (
                    'Current Plan'
                  ) : isDowngrade ? (
                    'Downgrade'
                  ) : plan.contactSales ? (
                    'Contact Sales'
                  ) : (
                    'Upgrade'
                  )}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* FAQ */}
      <div className="bg-[#1a1a1a] rounded-2xl border border-white/10 p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Frequently Asked Questions</h3>
        <div className="space-y-4 text-sm">
          <div>
            <p className="text-white font-medium">Can I change plans anytime?</p>
            <p className="text-white/60">Yes, you can upgrade or downgrade at any time. Changes take effect immediately.</p>
          </div>
          <div>
            <p className="text-white font-medium">What happens if I exceed my limits?</p>
            <p className="text-white/60">We'll notify you when you're approaching limits. Consider upgrading to continue without interruption.</p>
          </div>
          <div>
            <p className="text-white font-medium">How do I cancel?</p>
            <p className="text-white/60">You can cancel anytime from this page. You'll retain access until your billing period ends.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
